import { ExternalLink } from 'lucide-react';
import { createElement } from 'react';

import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';

import { commitIcon, formatCommitTime, toneBadgeClasses, toneDotClasses } from '../lib/commit-format';
import type { Commit, CommitTipoResumen } from '../updates.types';

interface CommitItemProps {
  commit: Commit;
  /** Resumen del tipo (etiqueta + tono) tal como lo clasifica el backend. */
  tipoInfo?: CommitTipoResumen;
  /** URL del commit en GitHub (si el repositorio remoto está identificado). */
  commitUrl?: string | null;
}

/** Fila del historial de cambios. */
export function CommitItem({ commit, tipoInfo, commitUrl }: CommitItemProps) {
  const etiqueta = tipoInfo?.label ?? commit.tipo;
  const hora = formatCommitTime(commit.fecha);

  return (
    <div className="group flex items-start gap-3 rounded-card border border-ink-100 bg-white px-4 py-3 shadow-soft transition-colors hover:border-brand-200">
      <span
        className={cn(
          'mt-0.5 flex size-8 shrink-0 items-center justify-center rounded-lg ring-1',
          toneBadgeClasses(tipoInfo?.tone),
        )}
      >
        {createElement(commitIcon(commit.tipo), { className: 'size-4', 'aria-hidden': true })}
      </span>

      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium leading-snug text-ink-900">{commit.descripcion}</p>

        <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-ink-400">
          <span
            className={cn(
              'inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 font-medium ring-1',
              toneBadgeClasses(tipoInfo?.tone),
            )}
          >
            <span
              aria-hidden="true"
              className={cn('size-1.5 rounded-full', toneDotClasses(tipoInfo?.tone))}
            />
            {etiqueta}
          </span>

          {commit.alcance ? <Badge variant="outline">{commit.alcance}</Badge> : null}

          <span className="truncate">{commit.autor}</span>
          {hora ? <span className="tabular-nums">{hora}</span> : null}

          <code className="rounded bg-ink-100 px-1.5 py-0.5 font-mono text-[11px] text-ink-600">
            {commit.hash_corto}
          </code>
        </div>
      </div>

      {commitUrl ? (
        <a
          href={commitUrl}
          target="_blank"
          rel="noreferrer noopener"
          aria-label={`Ver commit ${commit.hash_corto} en GitHub`}
          title="Ver el cambio en el repositorio"
          className="mt-0.5 flex size-7 shrink-0 items-center justify-center rounded-lg text-ink-300 opacity-0 transition-all group-hover:opacity-100 hover:bg-ink-100 hover:text-brand-600 focus-visible:opacity-100"
        >
          <ExternalLink className="size-3.5" aria-hidden="true" />
        </a>
      ) : null}
    </div>
  );
}
